import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CampaignSelector from './CampaignSelector';
import CopyResultsList from './CopyResultsList';
import CopyQuestionsDialogActions from './CopyQuestionsActions';

import type { Campaign } from '../types';

export interface CopyResult {
    campaignId: number;
    success: boolean;
    error?: string;
}

interface CopyQuestionsDialogProps {
    open: boolean;
    onClose: () => void;
    onCopy: (targetCampaignIds: number[]) => void;
    campaigns: Campaign[];
    isLoading?: boolean | false;
    isCopying?: boolean | false;
    currentCampaignId: number | null;
    copyResults?: CopyResult[] | null;
}

/**
 * Renders dialog for copying questions to other campaigns.
 *
 * @param {CopyQuestionsDialogProps} props
 * @returns {JSX.Element}
 */
const CopyQuestionsDialog: React.FC<CopyQuestionsDialogProps> = (props) => {
    const { open, onClose, onCopy, campaigns, isLoading = false, isCopying = false, currentCampaignId, copyResults = null } = props;

    const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>([]);

    const handleCopy = () => {
        onCopy(selectedCampaigns.map((campaign) => campaign.id));
    };

    const handleClose = () => {
        if (!isCopying) {
            onClose();
        }
    };

    // Exclude current campaign from the list of available campaigns
    const availableCampaigns = useMemo(
        () => campaigns.filter((campaign) => campaign.id !== currentCampaignId),
        [campaigns, currentCampaignId]
    );

    useEffect(() => {
        if (!open) {
            setSelectedCampaigns([]);
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>
                {copyResults ? 'Copy results' : 'Copy questions to other projects'}
            </DialogTitle>
            <DialogContent>
                {copyResults ? (
                    <CopyResultsList copyResults={copyResults} campaigns={campaigns} />
                ) : (
                    <CampaignSelector
                        campaigns={availableCampaigns}
                        selectedCampaigns={selectedCampaigns}
                        onSelectionChange={setSelectedCampaigns}
                        isLoading={isLoading}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <CopyQuestionsDialogActions
                    copyResults={copyResults}
                    isCopying={isCopying}
                    selectedCampaignsCount={selectedCampaigns.length}
                    onClose={handleClose}
                    onCopy={handleCopy}
                />
            </DialogActions>
        </Dialog>
    );
};

export default CopyQuestionsDialog;
